import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Checkbox,
    FormControlLabel,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    Security as SecurityIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Gavel as GavelIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';


import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { submitTerms, getDeclarationStatus, getApplicationId, checkAndTransferLabDetails } from '../services/api';
import { LoaderWrapper } from './index';
import { encryptText, encryptAppId } from '../utils/encryption';

const CorruptionDeclarationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [mounted, setMounted] = useState(false);
    const [agreedToDeclaration, setAgreedToDeclaration] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [headName, setHeadName] = useState("");
    const [designation, setDesignation] = useState("");
    const [userInfo, setUserInfo] = useState({
        orgId: '',
        userTypeId: 0,
        appId: null
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const head = params.get("headName");
        const desig = params.get("designation");
        const date = params.get("acceptanceDate");

        if (head) setHeadName(head);
        if (desig) setDesignation(desig);
        if (date) {
            // optional if you want to use somewhere
            console.log("Acceptance Date:", date);
        }

        setAgreedToDeclaration(true);
    }, [location.search]);

    const initializeDeclaration = useCallback(async () => {
        try {
            // Get user info from localStorage or session
            const storedOrgId = localStorage.getItem('orgId') || sessionStorage.getItem('orgId');
            const storedUserTypeId = localStorage.getItem('userTypeId') || sessionStorage.getItem('userTypeId') || '0';

            if (!storedOrgId) {
                toast.error('Organization information not found. Please login again.');
                navigate('/main/login');
                return;
            }

            setUserInfo({
                orgId: storedOrgId,
                userTypeId: storedUserTypeId,
                appId: null
            });

            // Check if declaration has already been submitted
            try {
                const statusResponse = await getDeclarationStatus(storedOrgId);
                if (statusResponse.data.success && statusResponse.data.hasSubmitted) {
                    toast.info('Declaration has already been submitted');
                    // Redirect to appropriate page based on user type
                    if (storedUserTypeId === '0' || storedUserTypeId === 0) {
                        navigate('/main/dashboard');
                    } else {
                        navigate('/main/assessor');
                    }
                    return;
                }
            } catch (error) {
                // If error, assume declaration hasn't been submitted and continue
            }

            // Get application ID
            try {
                const appResponse = await getApplicationId(storedOrgId);
                if (appResponse.data.success) {
                    setUserInfo(prev => ({
                        ...prev,
                        appId: appResponse.data.appId
                    }));
                    localStorage.setItem('appId', appResponse.data.appId); // Store appId for future use
                }
            } catch (error) {
                // Continue without app ID
            }

            // Check if user should be redirected to application form
            const storedLabTypeId = localStorage.getItem('labTypeId');
            if (storedUserTypeId === '4' && storedLabTypeId === '3') {
                // Get appId and orgId for encrypted navigation
                const appId = localStorage.getItem('appId');
                const orgId = localStorage.getItem('orgId');

                if (appId && orgId) {
                    // Encrypt appId and orgId for URL parameters
                    const encryptedAppId = encryptAppId(appId);
                    const encryptedOrgId = encryptText(orgId);

                    // Navigate with encrypted parameters
                    navigate(`/main/medical/laboratory-details?appId=${encryptedAppId}&orgId=${encryptedOrgId}`);
                } else {
                    // Fallback to navigation without parameters
                    navigate('/main/medical/laboratory-details');
                }
                return;
            }

        } catch (error) {
            // Error initializing declaration
        } finally {
            setInitialLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        setMounted(true);
        initializeDeclaration();
    }, [initializeDeclaration]);

    const prohibitedActivities = [
        'Assessor claiming travelling expenditure/local conveyance from the CABs in cash or by any other mode of payment.',
        'NABL prohibits any corrupt practices. Any violation shall lead to adverse action against both the CAB and the assessor.',
        'Seeking undue favors by assessor (viz. travel for personal use, shopping, sightseeing, events, gifts, mementos, job referrals etc.) or CABs offering such unwanted favors.',
        'Involvement in promotional/marketing of any brand/make/model of equipment, machinery, instruments, providing consultancy, training, business interests.'
    ];

    const handleSubmit = () => {
        if (!agreedToDeclaration) {
            toast.error('Please agree to the declaration before proceeding');
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleConfirmSubmission = async () => {
        setLoading(true);
        setShowConfirmDialog(false);

        try {
            const submissionData = {
                orgId: userInfo.orgId,
                userTypeId: userInfo.userTypeId,
                agreed: true,
                headName: headName,
                designation: designation
            };

            const response = await submitTerms(submissionData);

            if (response.data.success) {
                setSubmitted(true);
                // toast.success('Declaration accepted successfully!');

                // Check if user should be redirected to application form after declaration
                const storedUserTypeId = localStorage.getItem('userTypeId');
                const storedLabTypeId = localStorage.getItem('labTypeId');

                if (storedUserTypeId === '4' && storedLabTypeId === '3') {
                    toast.success('Declaration submitted! Redirecting to medical application form...');
                    setTimeout(() => {
                        // Get appId and orgId from localStorage
                        const appId = localStorage.getItem('appId');
                        const orgId = localStorage.getItem('orgId');

                        if (appId && orgId) {
                            // Encrypt appId and orgId for URL parameters
                            const encryptedAppId = encryptAppId(appId);
                            const encryptedOrgId = encryptText(orgId);

                            // Navigate with encrypted parameters
                            navigate(`/main/medical/laboratory-details?appId=${encryptedAppId}&orgId=${encryptedOrgId}`);
                        } else {
                            // Fallback to navigation without parameters
                            navigate('/main/medical/laboratory-details');
                        }
                    }, 2000);
                    return;
                }

                // Check if we need to prepare Form153 data
                const labTypeId = response.data.labTypeId;
                let redirectPath = response.data.redirectPath || '/main/dashboard';

                // Add encrypted appId and orgId to redirect path
                const appId = localStorage.getItem('appId');
                const orgId = localStorage.getItem('orgId');

                if (appId && orgId) {
                    const encryptedAppId = encryptAppId(appId);
                    const encryptedOrgId = encryptText(orgId);

                    // Check if redirectPath already has query parameters
                    const separator = redirectPath.includes('?') ? '&' : '?';
                    redirectPath = `${redirectPath}${separator}appId=${encryptedAppId}&orgId=${encryptedOrgId}`;
                }

                // Store labTypeId in localStorage for future use
                if (labTypeId) {
                    localStorage.setItem('labTypeId', labTypeId.toString());
                }

                // If redirecting to medical lab details and we have appId, prepare Form153 data
                const shouldPrepareForm153Data = (
                    (labTypeId === '3' || labTypeId === 3) &&
                    redirectPath.includes('application-form') &&
                    userInfo.appId
                );

                if (shouldPrepareForm153Data) {
                    try {
                        // toast.info('Preparing laboratory data...', { autoClose: 2000 });

                        const transferResponse = await checkAndTransferLabDetails(userInfo.appId, userInfo.orgId);

                        if (transferResponse.data.success) {
                            if (transferResponse.data.dataExists) {
                                // toast.success('Laboratory data is ready!', { autoClose: 2000 });
                            } else if (transferResponse.data.transferred) {
                                // toast.success('Laboratory data prepared successfully!', { autoClose: 2000 });
                            }
                        } else {
                            // If data transfer fails, still proceed
                            // toast.warning('Proceeding without data preparation', { autoClose: 3000 });
                        }
                    } catch (transferError) {
                        // If API call fails, still proceed to avoid blocking user flow
                        // toast.warning('Data preparation encountered an issue, but you can still proceed', { autoClose: 3000 });
                    }
                }

                // Redirect based on the response from backend
                setTimeout(() => {
                    if (redirectPath.includes('live_lang_lat')) {
                        // Handle location verification redirect
                        window.location.href = `/main${redirectPath}`;
                    } else if (redirectPath.includes('application-form')) {

                        // Navigate to dashboard which will check stage and redirect appropriately
                        navigate('/main/dashboard');
                    } else if (redirectPath.includes('medical')) {
                        // Use the enhanced redirectPath that already contains encrypted parameters
                        navigate(redirectPath);
                    } else if (redirectPath.includes('assessor')) {
                        navigate('/main/assessor');
                    } else {
                        navigate('/main/dashboard');
                    }
                }, shouldPrepareForm153Data ? 3000 : 2000); // Give more time if data preparation occurred
            } else {
                toast.error(response.data.message || 'Failed to submit declaration');
            }
        } catch (error) {
            let errorMessage = 'Failed to submit declaration';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };



    // Show loading spinner while initializing
    if (initialLoading) {
        return (
            <LoaderWrapper
                loading={true}
                type="fullscreen"
                message="Loading Declaration..."
            />
        );
    }

    return (
        <Box
            sx={{
                mt: 8,
                height: '100vh',
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Container maxWidth="xl">
                <Fade in={mounted} timeout={1000}>
                    <Paper
                        elevation={8}
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            background: 'white',
                            height: '95vh',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Main Content - Scrollable if needed */}
                        <Box sx={{
                            flex: 1,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'auto'
                        }}>
                            {/* Two Column Layout */}
                            <Box sx={{

                            }}>
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                        <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Terms & Conditions
                                    </Typography>

                                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                                    </Typography>

                                    <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        The Conformity Assessment Body {'{Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)}'} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:
                                    </Typography>
                                    <List sx={{ pl: 2, listStyleType: "decimal", "& .MuiListItem-root": { display: "list-item" } }}>
                                        {/* Point 1 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The Conformity Assessment Body (CAB) shall carry out its Testing / Calibration /
                                                        Medical / PTP / RMP / Biobank activities in such a way as to meet the requirements
                                                        of relevant standard (eg. ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034
                                                        or ISO 20387, whichever is applicable), appropriate NABL specific
                                                        criteria/application documents (wherever applicable) and other policies of NABL,
                                                        as relevant to the accredited scope at all times.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 2 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        CAB shall ensure that the latest versions of NABL documents are available with it
                                                        and are effectively implemented.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 3 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        CAB shall familiarize itself with all relevant standards, NABL documents etc.
                                                        applicable for particular accreditation scheme.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 4 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                            The CAB shall have a valid legal entity status as given below:
                                                        </Typography>

                                                        {/* Sub List */}
                                                        <List
                                                            sx={{
                                                                pl: 4,
                                                                mt: 1,
                                                                listStyleType: "lower-alpha",
                                                                "& .MuiListItem-root": { display: "list-item" },
                                                            }}
                                                        >
                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText
                                                                    primary="One Person Company (Registration certificate under The Companies Act, 2013)"
                                                                />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText
                                                                    primary="Limited Liability Partnership (Registration certificate under The Limited Liability Partnership Act, 2008)"
                                                                />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText
                                                                    primary="Company (Registration certificate under The Companies Act, 1956 or 2013)"
                                                                />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText
                                                                    primary="Societies / Trust (Registration certificate under Societies Registration Act, 1860 / Registration under The Indian Trusts Act, 1882)"
                                                                />
                                                            </ListItem>
                                                        </List>
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                        {/* Point 5 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        CAB (Conformity Assessment Body) shall provide all necessary information about its
                                                        antecedents as required by NABL which shall include but not limited to its background
                                                        information i.e. history, establishment, ownership, and organizational structure,
                                                        Key Personnel, Previous Experience, Legal Compliance, Disputes and Complaints if any,
                                                        Quality Management System, Financial Information if required, Conflict of Interest,
                                                        Accreditations and Certifications from any other body or Any other information,
                                                        deemed necessary by NABL to analyse and evaluate the CAB's eligibility for
                                                        accreditation. By submitting the application for accreditation, the CAB expressly
                                                        consents to the collection, use, and disclosure to NABL of the provided information
                                                        for the purpose of evaluating the accreditation application and ensuring compliance
                                                        with accreditation standards. NABL reserves the right to verify the accuracy and
                                                        authenticity of the provided information. Failure to provide accurate and complete
                                                        information or any attempt to withhold relevant details shall result in the rejection
                                                        of the accreditation application.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 6 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Accreditation granted by NABL signifies conformity of the laboratory’s management
                                                        system and technical competence with the applicable accreditation criteria at the
                                                        time of assessment and does not constitute an endorsement, certification, warranty,
                                                        or guarantee by NABL of the laboratory’s test or calibration results, services, or
                                                        continued performance. NABL shall not be responsible or liable for the accuracy,
                                                        validity, or use of any test or calibration results, reports, opinions, or services
                                                        provided by the accredited laboratory, nor for any contractual, commercial, or
                                                        statutory obligations between the laboratory and its clients or third parties.
                                                        Nothing contained herein shall be construed as limiting NABL’s functions, powers,
                                                        or rights under the accreditation agreement, nor as excluding liability arising from
                                                        fraud or willful misconduct or action specifically attributable to NABL under the
                                                        applicable law.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 7 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Accreditation granted by NABL is voluntary in nature and does not replace, waive,
                                                        or substitute compliance with any applicable law, rule, regulation, or statutory
                                                        requirement, except where such accreditation is expressly mandated by a Government
                                                        authority or regulatory body. In the event of any inconsistency, conflict, or
                                                        contradiction between the provisions of this accreditation agreement, or any NABL
                                                        guideline and the applicable laws, rules, regulations, or statutory directions in
                                                        force, the provisions of such applicable law shall prevail to the extent of such
                                                        inconsistency.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 8 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Accreditation granted by NABL is limited solely to the assessment, recognition and
                                                        attestation of the technical competence of the Conformity Assessment Body (CAB) in
                                                        accordance with applicable accreditation criteria. Such accreditation shall not be
                                                        construed as a permission, authorization, license, or approval to conduct any
                                                        business, commercial, clinical, diagnostic, or operational activity.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 9 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        It is the responsibility of CAB to obtain and maintain statutory and regulatory
                                                        approvals, licenses, registrations and permissions as required under the applicable
                                                        laws and abide by the National / Regional / State / Local regulatory requirements /
                                                        Acts / Rules / Legal orders / Court Decisions / Orders issued by Government /
                                                        Statutory Bodies as applicable and effective from time to time.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 10 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall identify and define various activities which they are involved in,
                                                        and ensure that it does not lead to any potential conflict of interest.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 11 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall have available qualified and trained personnel for applied and/or
                                                        accredited scope including sampling (wherever applicable).
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 12 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Wherever applicable, the CAB shall follow the test methods as mentioned in the
                                                        current National or International standards and/or as stipulated by regulatory
                                                        bodies. Where such methods do not exist, other validated methods may be followed.
                                                        In case CAB uses in-house validated methods, the validation data should be
                                                        submitted along with the application.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 13 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        CAB shall ensure the capability with respect to applied scope of accreditation
                                                        (availability of resources not limited to personnel, equipment, CRM, method
                                                        validation etc.) before applying to NABL and thereafter at all times during the
                                                        accredited period.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 14 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall promptly inform NABL of any changes in its organization, personnel,
                                                        facilities, scope of services, or any other significant factors that may impact
                                                        its accreditation status.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 15 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Applicant CAB shall successfully participate in at least one Proficiency Testing
                                                        (PT) program prior to gaining accreditation in each discipline applied. There may
                                                        be situation where PT is not available. Requirements are specified in NABL 163 and
                                                        the same shall be followed for PT participation or otherwise.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {/* Point 16 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        CAB shall submit the completed application for renewal of accreditation well in
                                                        advance (give sufficient time to process) prior to the expiry of accreditation and
                                                        also agree to undergo assessment as per the schedule proposed by NABL to maintain
                                                        continuity in accreditation cycle. Application submitted after expiry of
                                                        accreditation will be considered as fresh application. The assessment (onsite)
                                                        interval shall not exceed 24 months and in case if exceeds, then it will be treated
                                                        afresh.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 17 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                            The CAB shall offer full cooperation to NABL or its representative or member of
                                                            team evaluating/assessing during the conduct of assessments (including onsite
                                                            surveillance), any additional assessments, such as additional surveillance
                                                            assessments based on risk, unannounced assessment/visit (including visit for
                                                            investigation of complaints related to NABL accreditation against the CAB). The
                                                            cooperation by the CAB shall include access to, but not limited to, the following
                                                            activities:
                                                        </Typography>

                                                        <List
                                                            sx={{
                                                                pl: 4,
                                                                mt: 1,
                                                                listStyleType: "lower-alpha",
                                                                "& .MuiListItem-root": { display: "list-item" },
                                                            }}
                                                        >
                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Undergoing assessments in stipulated time intervals / whenever NABL considered it as required." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Providing access to all areas of operations related to CAB including permanent site, site, mobile / temporary, subcontractor / external provider premises, wherever relevant and applicable." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Undertaking any check / inspection to verify the capability of the CAB for the applied and/or accredited scope." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Witnessing the activities being performed relevant to accreditation." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Assessing the competence of the staff (including staff working in shift operations / at permanent site, at site, at mobile facility) during assessment." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Access to all relevant information and documentation and records." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Access to those documents that provide insight into the level of independence and impartiality of the CAB from its own activities and its related bodies, if applicable." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Access to all records pertaining to competence of the CAB personnel." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Assessing the competence of the persons who are responsible to report, review and authorization of the results." />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="Assessing the competence of the persons who are responsible for opinion and interpretation." />
                                                            </ListItem>
                                                        </List>
                                                    </>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 18 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall maintain the highest level of impartiality, integrity, and
                                                        professionalism throughout the accreditation process as well as accreditation
                                                        period. It shall avoid any actions or relationships that could compromise its
                                                        independence or objectivity.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 19 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall establish and maintain an effective management system, adhering to
                                                        recognized international or any other standards by NABL, to ensure consistent and
                                                        reliable conformity assessment services.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 20 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall cooperate fully with NABL during assessments, audits,
                                                        investigations, and any other activities related to the accreditation process. It
                                                        shall provide all necessary information and access required by NABL to evaluate its
                                                        compliance with accreditation standards.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 21 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall have, where applicable, legally enforceable arrangements with their
                                                        customer/client that commit the customers/clients to provide, on request, access to
                                                        NABL assessment team to assess the CAB’s performance when carrying out conformity
                                                        assessment activities at the customer/client’s site.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 22 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                            An accredited CAB shall also demonstrate the following during the validity period
                                                            of its accreditation:
                                                        </Typography>

                                                        <List
                                                            sx={{
                                                                pl: 4,
                                                                mt: 1,
                                                                listStyleType: "lower-alpha",
                                                                "& .MuiListItem-root": { display: "list-item" },
                                                            }}
                                                        >
                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="compliance with the terms of this agreement" />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="continuing conformity with the relevant standard (eg. ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034 or ISO 20387, whichever is applicable) and appropriate NABL specific criteria/application documents (wherever applicable)" />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="continuing competence within the scope of its accreditation" />
                                                            </ListItem>

                                                            <ListItem sx={{ pl: 0 }}>
                                                                <ListItemText primary="willingness to give such undertakings as NABL may reasonably require" />
                                                            </ListItem>
                                                        </List>
                                                    </>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 23 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        During the accreditation period, the CAB shall claim accreditation for only those
                                                        premises, and scope for which it has been accredited (as stated in Accreditation
                                                        Certificate and scope of Accreditation).
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {/* Point 24 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Applicant CABs shall not claim to be NABL Accredited or use NABL symbol/logo before
                                                        receiving the communication regarding grant of accreditation from NABL. In case it
                                                        is found that CAB is claiming to be NABL Accredited or using NABL symbol/logo in
                                                        applicant stage or misleading customers about its accreditation status in any way,
                                                        this may lead to rejection of application or termination of the assessment process
                                                        or denial of accreditation. Legal action may also be initiated.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 25 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Accredited CAB shall adhere to NABL 133 for the use of NABL symbol, use of NABL
                                                        Accredited CAB Combined ILAC MRA Mark as well as any claim of accreditation of the
                                                        CAB. CAB shall ensure that it fully conforms to the requirements of NABL and
                                                        ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034 or ISO 20387 (as
                                                        applicable) for claiming accreditation status while making references of its NABL
                                                        accreditation in communication media. During the period of suspension or after
                                                        withdrawal of accreditation, suitable legal action may be taken by NABL in case CAB
                                                        falsely claims to be NABL accredited.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 26 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Accredited CAB shall ensure that the copy of the test report/certificate, medical
                                                        test report/certificate, calibration certificate/report, PT report and RM document
                                                        stored/retained shall be an exact replica of the report issued to the customer
                                                        including the format of the report header & footer, and NABL symbol.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 27 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        CAB shall use NABL accredited CAB Combined ILAC MRA Mark only after obtaining
                                                        written permission from NABL and the usage shall be restricted to the period of
                                                        validity of the permission as informed by NABL.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 28 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        Accredited CAB shall make it clear to its customer that its activities falling
                                                        under accredited scope in no way imply that the product so tested or equipment
                                                        calibrated or PT scheme offered or Reference Material (RM) produced is approved by
                                                        NABL.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 29 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The accredited CAB is neither allowed to make misleading / unauthorized statement
                                                        regarding its accreditation nor refer to accreditation in such a way so as to imply
                                                        that a product, process, service, management system or person is approved by NABL.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 30 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall pay application fees for accreditation, renewal of accreditation,
                                                        expenses towards travel, boarding & lodging for any kind of assessment including
                                                        supplementary visit, on-site surveillance, desktop surveillance, re-assessment and
                                                        annual accreditation fees as determined from time to time, by NABL.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 31 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                            The CAB shall inform NABL without delay (within 15 days) of significant changes
                                                            relevant to accreditation, such as:
                                                        </Typography>

                                                        <List
                                                            sx={{
                                                                pl: 4,
                                                                mt: 1,
                                                                listStyleType: "lower-alpha",
                                                                "& .MuiListItem-root": { display: "list-item" },
                                                            }}
                                                        >
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="its legal, commercial, ownership or organizational status." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="its resources not limited to personnel, facilities, equipment to manage and perform its activities, authorized personnel responsible to report, review and authorization of the results." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="premises." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="scope of accreditation." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="External Service Provider/Subcontractor, Production and/or characterization techniques (PTP/RMP)." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="Major changes in the operation of the CAB, for example, closure of the CAB for long duration, say more than 6 months." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="any other factor that may affect the ability of the CAB to fulfill the requirements of NABL accreditation including applicable policies & standards." /></ListItem>
                                                            <ListItem sx={{ pl: 0 }}><ListItemText primary="any other change in information submitted in application." /></ListItem>
                                                        </List>
                                                    </>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 32 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall regularly visit NABL website to keep itself updated with the latest versions of NABL documents as well as the NABL newsletter or any other relevant information concerning accreditation provided on the NABL website.</Typography>} />
                                        </ListItem>

                                        {/* Point 33 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall always have a dedicated person responsible for implementation of Management System.</Typography>} />
                                        </ListItem>

                                        {/* Point 34 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall unambiguously and truthfully declare the name of any individual and organization that has provided consultancy for preparing towards NABL accreditation, at the relevant section in the application form.</Typography>} />
                                        </ListItem>

                                        {/* Point 35 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Where a CAB subcontracts a substantial or critical part of the accredited activities that work shall be placed with another CAB that is accredited by NABL.</Typography>} />
                                        </ListItem>

                                        {/* Point 36 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The accredited CAB shall commit to comply with the changes initiated by NABL in its accreditation criteria, policies and procedures. The CAB shall inform NABL when such alterations under the agreed time frame have been completed.</Typography>} />
                                        </ListItem>

                                        {/* Point 37 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall take all necessary actions and discharge all non-conformities raised during the assessment within 30 days to avoid any adverse decisions against it as detailed in NABL 216. The same shall be verified to the satisfaction of NABL. The final decision on accreditation shall rest with NABL.</Typography>} />
                                        </ListItem>

                                        {/* Point 38 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall not use its NABL accreditation in such a manner as to bring NABL into disrepute.</Typography>} />
                                        </ListItem>

                                        {/* Point 39 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>At any point in the application or initial process, if there is evidence of fraudulent behavior and/or if the CAB has intentionally provided false information or conceals information, NABL shall reject the application or terminate the assessment process or denial of accreditation.</Typography>} />
                                        </ListItem>

                                        {/* Point 40 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>If evidence is found of fraudulent behaviour or the CAB intentionally provides false information or conceals information, NABL shall initiate adverse action as per NABL 216.</Typography>} />
                                        </ListItem>

                                        {/* Point 41 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The accredited CAB upon suspension, withdrawal of accreditation or debar from reapplying (however determined) shall forthwith discontinue claim of accreditation in any form in all advertising material and return the accreditation certificates to NABL, if applicable.</Typography>} />
                                        </ListItem>

                                        {/* Point 42 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall inform its affected clients of the suspension, scope reduction, withdrawal of accreditation (and debar from reapplying, if applicable) and the associated consequences, without undue delay.</Typography>} />
                                        </ListItem>

                                        {/* Point 43 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Accredited CAB can voluntarily withdraw accreditation by giving notice in writing to NABL (and by surrendering the accreditation certificates, applicable) after clearing the outstanding amount, if any.</Typography>} />
                                        </ListItem>

                                        {/* Point 44 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall inform NABL, if any of the proposed assessor(s) happens to be their consultant or associated with the CAB in any other capacity (personal or professional).</Typography>} />
                                        </ListItem>

                                        {/* Point 45 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>NABL shall not be held responsible for any legal or financial liability arising out of activities of any of its accredited CAB involving in any accidental or consequential damages to personnel / equipment / products at any time.</Typography>} />
                                        </ListItem>

                                        {/* Point 46 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall not offer inducement of any kind such as any gifts or any kind of payments in cash or any undue favour to the assessment team members. In case of any violation, NABL shall initiate adverse action as per NABL 216.</Typography>} />
                                        </ListItem>

                                        {/* Point 47 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>All information obtained by NABL and its personnel in NABL's direct dealing with the accredited CABs in all stages from application onwards, other than the information in public domain shall be treated as confidential. When NABL is required by law or authorized by contractual arrangements to release the confidential information, the CAB shall be notified unless prohibited by law.</Typography>} />
                                        </ListItem>

                                        {/* Point 48 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CABs acknowledges and agrees to maintain strict confidentiality with regard to all sensitive information, records, data, and proprietary materials obtained or accessed during the accreditation process. CABs shall not disclose, share, or disseminate any confidential information obtained from NABL or any other CABs, clients, or stakeholders participating in the accreditation process.</Typography>} />
                                        </ListItem>

                                        {/* Point 49 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>If the CAB engages subcontractors, consultants, or third-party service providers to assist in the accreditation process, the CAB shall declare these to NABL and ensure that such entities/individuals also comply with the same level of confidentiality and other obligations as set forth herein.</Typography>} />
                                        </ListItem>
                                        {/* Point 50 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        The CAB shall implement appropriate measures to protect the confidentiality and
                                                        security of any personal data or sensitive information received or processed during
                                                        the accreditation process. CAB shall ensure full compliance with the provisions of
                                                        the Digital Personal Data Protection Act, 2023, and rules made thereunder,
                                                        including obligations relating to lawful processing, consent, purpose limitation,
                                                        data security, data retention, and data principal rights.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 51 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB acknowledges that any breach of the confidentiality obligations outlined in this clause may result in the termination of the accreditation process, and may also give rise to legal liabilities.</Typography>} />
                                        </ListItem>

                                        {/* Point 52 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The confidentiality obligations outlined in this clause shall continue to apply even after the conclusion of the accreditation process, regardless of whether accreditation is granted or not, except as required by law.</Typography>} />
                                        </ListItem>

                                        {/* Point 53 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Information about the conformity assessment body obtained from sources other than the conformity assessment body (e.g. complainant, regulators) shall be confidential between the conformity assessment body and NABL. The provider of this information is confidential to NABL and shall not be shared with the conformity assessment body, unless agreed by the source.</Typography>} />
                                        </ListItem>

                                        {/* Point 54 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall indemnify and hold NABL, its CEO, officers, directors, employees, and representatives protected against any claims, demands, losses, damages, penalties, costs, or legal proceedings arising out of or in connection with any non-compliance by the CAB with applicable laws, rules or regulations including the Digital Personal Data Protection Act, 2023, or any act, omission, negligence in the conduct of testing, calibration, sampling, inspection, certification related laboratory activities or associated services or misuse, misrepresentation or unauthorised reference to NABL accreditation or any activity prejudicial to public interest, public health or national interest.</Typography>} />
                                        </ListItem>

                                        {/* Point 55 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB agrees that NABL shall not be liable for any damages, losses, or expenses incurred by the CAB due to the rejection or withdrawal of its accreditation application or accreditation status, regardless of the reasons for such rejection or withdrawal.</Typography>} />
                                        </ListItem>

                                        {/* Point 56 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>NABL shall not be liable for any failure or delay in performing its obligations under the accreditation process due to force majeure events or circumstances beyond its reasonable control.</Typography>} />
                                        </ListItem>

                                        {/* Point 57 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall maintain adequate and appropriate insurance coverage to protect itself against any liabilities, claims, or damages that may arise during the accreditation process.</Typography>} />
                                        </ListItem>

                                        {/* Point 58 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB seeking accreditation hereby agrees to abide by all applicable rules, regulations, guidelines, and standards set forth by NABL and other relevant authorities throughout the accreditation process and the provision of conformity assessment services.</Typography>} />
                                        </ListItem>

                                        {/* Point 59 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall ensure strict compliance with the accreditation criteria, policies, procedures, and guidelines issued by NABL. The CAB shall implement any necessary corrective actions to address any non-conformities identified during assessments or audits.</Typography>} />
                                        </ListItem>

                                        {/* Point 60 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>After exhausting all remedies provided by NABL to resolve any issue, the CAB may approach courts at New Delhi and none other and shall be governed by the laws of India.</Typography>} />
                                        </ListItem>

                                        {/* Point 61 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                        If the acts of the CAB are violative of NABL policy, rules, regulations or the CAB
                                                        is found indulged in fraudulent and unethical activities or any such activities
                                                        adversely affecting the integrity of NABL or violating accreditation terms, then
                                                        NABL shall be at liberty to take appropriate action as per its policy, rules and
                                                        regulations which may extend to removal of CABs from the NABL website. In case a
                                                        CAB has approached the court against the decision or action of NABL, the
                                                        accreditation certificate (related to the legal entity), and if there are multiple
                                                        CABs accredited by NABL at different locations belonging to the same legal entity,
                                                        then each one of the accredited CABs shall remain removed from the NABL website.
                                                        Existing activities of all CABs linked to that legal entity shall be put on hold
                                                        till pendency or resolution of the matter depending on gravity. CAB shall not
                                                        submit any application during pendency of court proceedings and any such
                                                        application shall not be entertained by NABL.
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>

                                        {/* Point 62 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB acknowledges and agrees that NABL shall not be held responsible or liable, directly or indirectly, for the technical competence, results, reports, opinions, decisions, services, business practices, contractual arrangements or any acts or omissions of the laboratory and/or its clients. NABL accreditation shall not be represented, used or construed as an endorsement, approval, supervision, guarantee or assumption of responsibility by NABL.</Typography>} />
                                        </ListItem>

                                        {/* Point 63 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>In the event that a CAB is found or is reasonably suspected to have engaged in activities contrary to applicable law, NABL regulations, accreditation criteria, public interest, public health, national interest or data protection obligations, NABL shall be entitled to initiate appropriate action including but not limited to suspension, reduction of scope, withdrawal of accreditation or debarment in accordance with NABL 216 and other applicable NABL documents.</Typography>} />
                                        </ListItem>

                                        {/* Point 64 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Without prejudice to the aforesaid rights and remedies, NABL shall reserve the right to initiate appropriate contractual or legal action as may be necessary to enforce the terms of the accreditation agreement where the acts or omissions of the CAB constitute breach of contractual obligations, fraudulent activities, unethical practices, negligence, or misconduct causing prejudice to NABL’s legal position, reputation, or the integrity and credibility of the accreditation system.</Typography>} />
                                        </ListItem>

                                        {/* Point 65 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Repeat violation of terms & conditions and commitments given by CAB to NABL shall be dealt strictly as per NABL 216 and in accordance with the applicable law.</Typography>} />
                                        </ListItem>

                                        {/* Point 66 */}
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The existing CAB owner(s) shall be responsible for all the related compliances and violations (if any) for the previous and current CAB application(s) processed by NABL.</Typography>} />
                                        </ListItem>
                                    </List>

                                </Box>
                                {/* Right Column - Additional Info & Declaration */}
                                <Box>
                                    <Box sx={{ mb: 3 }}>
                                        <TextField
                                            fullWidth
                                            label="Name of Head of the CAB"
                                            value={headName}
                                            margin="normal"
                                            InputProps={{ readOnly: true }}
                                        />

                                        <TextField
                                            fullWidth
                                            label="Designation of Head of the CAB"
                                            value={designation}
                                            margin="normal"
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Box>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={true}
                                                disabled
                                                sx={{
                                                    color: '#1e3c72',
                                                    '&.Mui-checked': { color: '#1e3c72' }
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                                                By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse National Accreditation Board for Testing and Calibration Laboratories Doc. No.: NABL 131 Terms & Conditions for Obtaining & Maintaining NABL Accreditation decision as per NABL 216 ‘Procedures for Dealing with Adverse Decisions’ including legal action. NABL reserves the right to suspend or withdraw accreditation in case of established irregularities without show cause notice or opportunity for hearing under exceptional circumstances and for the reasons including but not limited to the non-compliance, misconduct, non- remedial action, non- payment of fee, legal and regulatory compliance, emergencies and public interest. NABL shall notify the CAB promptly of such decision and provide reasons for the suspension, or withdrawal, The CAB shall have the right to appeal the decision of suspension, or withdrawal, in accordance with the established appeal mechanism and procedures prescribed by NABL.
                                            </Typography>
                                        }
                                        sx={{}}
                                    />

                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>

                {/* Success State */}
                {submitted && (
                    <Fade in={true} timeout={1000}>
                        <Paper
                            elevation={8}
                            sx={{
                                borderRadius: 3,
                                p: 4,
                                textAlign: 'center',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                background: 'white',
                                zIndex: 1000,
                                minWidth: 400,
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#047857' }}>
                                Declaration Submitted Successfully
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Thank you for your commitment to maintaining integrity.
                            </Typography>
                        </Paper>
                    </Fade>
                )}

                {/* Confirmation Dialog */}
                <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                        <SecurityIcon sx={{ fontSize: 28, color: '#1e3c72', mr: 1 }} />
                        Confirm Declaration
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body1">
                            Are you sure you want to submit this declaration?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 2 }}>
                        <Button onClick={() => setShowConfirmDialog(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmSubmission}
                            variant="contained"
                            disabled={loading}
                            sx={{
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                                },
                                '&:disabled': {
                                    background: '#ccc',
                                },
                            }}
                        >
                            <LoaderWrapper loading={loading} type="button">
                                {loading ? 'Submitting...' : 'Confirm'}
                            </LoaderWrapper>
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default CorruptionDeclarationPage;
